import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import { alpha, useTheme } from "@mui/material/styles";

import WorkOutlineIcon from "@mui/icons-material/WorkOutlined";
import InsightsIcon from "@mui/icons-material/Insights";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedIcon from "@mui/icons-material/Verified";
import DescriptionIcon from "@mui/icons-material/Description";
import GitHubIcon from "@mui/icons-material/GitHub";

import { useQuery } from "@tanstack/react-query";

import { PageHeader } from "@/components/PageHeader";
import { UsageWarningBanner } from "@/components/UsageWarningBanner";
import { SubscriptionStatusBanner } from "@/components/SubscriptionStatusBanner";
import { useCandidate } from "@/context/CandidateContext";
import { useApplications, type ApplicationEntry, type ApplicationStatus } from "@/context/ApplicationContext";
import { useAuth } from "@/hooks/useAuth";
import { fetchProfile, profileQueryKey } from "@/lib/queries/profile";
import { STAGE_KEYS, STAGE_LABELS, type StageKey } from "@/lib/stages";

// Pipeline columns mirror the Kanban board on /app/jobs
type PipelineColumn = "Applied" | StageKey | "Offer" | "Rejected" | "Withdrawn";
const PIPELINE_COLUMNS: PipelineColumn[] = [
  "Applied",
  ...STAGE_KEYS.filter((k) => k !== "offer"),
  "Offer",
  "Rejected",
  "Withdrawn",
];
const PIPELINE_LABEL: Record<PipelineColumn, string> = {
  Applied: "Applied",
  recruiter: STAGE_LABELS.recruiter,
  hiring_manager: STAGE_LABELS.hiring_manager,
  portfolio: STAGE_LABELS.portfolio,
  panel: STAGE_LABELS.panel,
  leadership: STAGE_LABELS.leadership,
  offer: "Offer",
  Offer: "Offer",
  Rejected: "Rejected",
  Withdrawn: "Withdrawn",
};
const PIPELINE_COLOR: Record<PipelineColumn, "default" | "primary" | "info" | "warning" | "success" | "error" | "secondary"> = {
  Applied: "info",
  recruiter: "primary",
  hiring_manager: "primary",
  portfolio: "secondary",
  panel: "secondary",
  leadership: "warning",
  offer: "success",
  Offer: "success",
  Rejected: "error",
  Withdrawn: "default",
};

export const Route = createFileRoute("/app/")({
  component: DashboardPage,
  head: () => ({ meta: [{ title: "Dashboard — JobYoda" }] }),
});

const STATUS_META: Record<
  ApplicationStatus,
  { color: "default" | "primary" | "success" | "error" | "warning" | "info" }
> = {
  Applied: { color: "info" },
  Interview: { color: "warning" },
  Offer: { color: "success" },
  Rejected: { color: "error" },
  Withdrawn: { color: "default" },
};

function DashboardPage() {
  const theme = useTheme();
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useCandidate();
  const { applications, loading: appsLoading, currentStageFor } = useApplications();
  const userId = user?.id ?? null;

  const { data: profileRow } = useQuery({
    queryKey: userId ? profileQueryKey(userId) : ["profile", "anon"],
    queryFn: () => fetchProfile(userId as string),
    enabled: !!userId,
  });

  const stats = useMemo(() => {
    const total = applications.length;
    const byStatus = applications.reduce<Record<ApplicationStatus, number>>(
      (acc, a) => {
        acc[a.status] = (acc[a.status] ?? 0) + 1;
        return acc;
      },
      { Applied: 0, Interview: 0, Offer: 0, Rejected: 0, Withdrawn: 0 },
    );
    const active = byStatus.Applied + byStatus.Interview + byStatus.Offer;
    const decided = byStatus.Offer + byStatus.Rejected + byStatus.Withdrawn;
    const interviewRate = total ? Math.round(((byStatus.Interview + byStatus.Offer) / total) * 100) : 0;
    const offerRate = decided ? Math.round((byStatus.Offer / decided) * 100) : 0;
    const scored = applications.filter((a) => typeof a.matchScore === "number");
    const avgMatch = scored.length
      ? scored.reduce((s, a) => s + (a.matchScore ?? 0), 0) / scored.length
      : 0;

    // Bucket apps into the same columns as the Kanban board
    const byColumn: Record<PipelineColumn, number> = {
      Applied: 0,
      recruiter: 0,
      hiring_manager: 0,
      portfolio: 0,
      panel: 0,
      leadership: 0,
      offer: 0,
      Offer: 0,
      Rejected: 0,
      Withdrawn: 0,
    };
    for (const a of applications) {
      if (a.status === "Applied") byColumn.Applied += 1;
      else if (a.status === "Rejected") byColumn.Rejected += 1;
      else if (a.status === "Withdrawn") byColumn.Withdrawn += 1;
      else if (a.status === "Offer") byColumn.Offer += 1;
      else {
        // Interview — map to current stage column
        const cs = currentStageFor(a.id);
        const key = (cs?.stage_key as StageKey | undefined) ?? "hiring_manager";
        if (key === "offer") byColumn.Offer += 1;
        else byColumn[key] += 1;
      }
    }

    return { total, active, byStatus, interviewRate, offerRate, avgMatch, byColumn };
  }, [applications, currentStageFor]);

  const recent = useMemo(() => {
    return [...applications]
      .sort((a, b) => (a.appliedAt < b.appliedAt ? 1 : -1))
      .slice(0, 5);
  }, [applications]);

  const profileCompletion = useMemo(() => {
    const has = (v: unknown) =>
      typeof v === "string" ? v.trim().length > 0 : v !== null && v !== undefined;
    const checks = [
      { label: "First name", done: has(profileRow?.first_name), important: true },
      { label: "Last name", done: has(profileRow?.last_name), important: true },
      { label: "Phone", done: has(profileRow?.phone), important: true },
      { label: "Location", done: has(profileRow?.location), important: true },
      { label: "LinkedIn URL", done: has(profileRow?.linkedin_url) || !!profile.linkedin.profileUrl, important: true },
      { label: "GitHub URL", done: has(profileRow?.github_url) || !!profile.github.username, important: false },
      { label: "Portfolio URL", done: has(profileRow?.portfolio_url) || profile.portfolio.links.length > 0, important: false },
      { label: "Pronouns", done: has(profileRow?.pronouns), important: false },
      { label: "Work authorization", done: has(profileRow?.work_authorization), important: true },
      { label: "Sponsorship answer", done: profileRow?.requires_sponsorship !== null && profileRow?.requires_sponsorship !== undefined, important: true },
      { label: "Gender (EEO)", done: has(profileRow?.gender), important: false },
      { label: "Race / Ethnicity (EEO)", done: has(profileRow?.race_ethnicity), important: false },
      { label: "Hispanic / Latino (EEO)", done: profileRow?.hispanic_latino !== null && profileRow?.hispanic_latino !== undefined, important: false },
      { label: "Veteran status (EEO)", done: has(profileRow?.veteran_status), important: false },
      { label: "Disability status (EEO)", done: has(profileRow?.disability_status), important: false },
      { label: "Resume parsed", done: profile.resume.chronologicalExperience.length > 0, important: true },
    ];
    const done = checks.filter((c) => c.done).length;
    return { done, total: checks.length, pct: Math.round((done / checks.length) * 100), checks };
  }, [profileRow, profile]);

  const nextAction = useMemo(() => {
    if (!profile.resume.rawText) {
      return {
        title: "Upload your resume",
        body: "Drop your PDF on the Profile page so we can extract your experience and skills.",
        cta: "Go to Profile",
        to: "/app/profile" as const,
        icon: <DescriptionIcon />,
      };
    }
    if (!profile.github.username) {
      return {
        title: "Connect your GitHub",
        body: "Link your GitHub to verify languages, repos, and engineering signal.",
        cta: "Add GitHub",
        to: "/app/profile" as const,
        icon: <GitHubIcon />,
      };
    }
    if (profile.verifiedSkills.length === 0) {
      return {
        title: "Confirm your skills",
        body: "Review and edit the verified skills extracted from your sources.",
        cta: "Edit skills",
        to: "/app/profile" as const,
        icon: <VerifiedIcon />,
      };
    }
    if (applications.length === 0) {
      return {
        title: "Score your first job",
        body: "Paste a JD and get a 1–10 success score with strengths and gaps.",
        cta: "Open Success Score",
        to: "/app/match" as const,
        icon: <InsightsIcon />,
      };
    }
    if (stats.byStatus.Interview > 0) {
      return {
        title: "Prep for your interview",
        body: `${stats.byStatus.Interview} interview${stats.byStatus.Interview > 1 ? "s" : ""} in progress. Run a coaching session.`,
        cta: "Open Interview",
        to: "/app/interview" as const,
        icon: <RocketLaunchIcon />,
      };
    }
    return {
      title: "Generate tailored materials",
      body: "Create a tailored resume and cover letter for your next application.",
      cta: "Open Materials",
      to: "/app/generate" as const,
      icon: <DescriptionIcon />,
    };
  }, [profile, applications, stats]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();
  const name = (() => {
    const meta = (user?.user_metadata ?? {}) as {
      first_name?: string;
      given_name?: string;
      full_name?: string;
      name?: string;
    };
    const fromMeta =
      meta.first_name ||
      meta.given_name ||
      (meta.full_name ?? meta.name ?? "").trim().split(/\s+/)[0];
    if (fromMeta) return fromMeta;
    const local = user?.email?.split("@")[0] ?? "";
    const first = local.split(/[._-]/)[0] ?? "";
    return first ? first.charAt(0).toUpperCase() + first.slice(1) : "there";
  })();

  return (
    <Box>
      <PageHeader
        title={`${greeting}, ${name}`}
        subtitle="Your career pipeline at a glance."
      />

      <SubscriptionStatusBanner />
      <UsageWarningBanner />

      {/* Stat strip */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            label="Active applications"
            value={String(stats.active)}
            icon={<WorkOutlineIcon />}
            tint={theme.palette.primary.main}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            label="In interview"
            value={String(stats.byStatus.Interview)}
            icon={<HourglassEmptyIcon />}
            tint={theme.palette.warning.main}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            label="Offers"
            value={String(stats.byStatus.Offer)}
            icon={<EmojiEventsOutlinedIcon />}
            tint={theme.palette.success.main}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            label="Avg success score"
            value={stats.avgMatch ? stats.avgMatch.toFixed(1) : "—"}
            icon={<InsightsIcon />}
            tint={theme.palette.info.main}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* Next best action */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(
                theme.palette.primary.main,
                0.02,
              )} 100%)`,
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.25),
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    width: 48,
                    height: 48,
                  }}
                >
                  {nextAction.icon}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>
                    Next best action
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {nextAction.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {nextAction.body}
                  </Typography>
                  <Button
                    component={Link}
                    to={nextAction.to}
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ mt: 2 }}
                  >
                    {nextAction.cta}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Pipeline breakdown */}
          <Card sx={{ mt: 2 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h6">Pipeline breakdown</Typography>
                <Button
                  component={Link}
                  to="/app/applications"
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                >
                  Open Kanban
                </Button>
              </Stack>
              {stats.total === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No applications yet — they'll appear here as you add them.
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {PIPELINE_COLUMNS.map((col) => {
                    const count = stats.byColumn[col];
                    if (!count) return null;
                    const pct = stats.total ? (count / stats.total) * 100 : 0;
                    const color = PIPELINE_COLOR[col];
                    return (
                      <Box key={col}>
                        <Stack
                          direction="row"
                          sx={{ alignItems: "center", justifyContent: "space-between", mb: 0.5 }}
                        >
                          <Chip
                            label={PIPELINE_LABEL[col]}
                            size="small"
                            color={color}
                            variant="outlined"
                          />
                          <Typography variant="body2" color="text.secondary">
                            {count} · {Math.round(pct)}%
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          color={color === "default" ? "primary" : color}
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Box>
                    );
                  })}
                  {PIPELINE_COLUMNS.every((c) => !stats.byColumn[c]) && (
                    <Typography variant="body2" color="text.secondary">
                      No applications in pipeline yet.
                    </Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={3}>
                    <MiniMetric label="Interview rate" value={`${stats.interviewRate}%`} />
                    <MiniMetric label="Offer rate (decided)" value={`${stats.offerRate}%`} />
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right column: profile completion + recent activity */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack
                direction="row"
                sx={{ alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography variant="h6">Profile completion</Typography>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                  {profileCompletion.pct}%
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {profileCompletion.done} of {profileCompletion.total} fields filled
              </Typography>
              <LinearProgress
                variant="determinate"
                value={profileCompletion.pct}
                color={
                  profileCompletion.pct < 50
                    ? "error"
                    : profileCompletion.pct < 85
                      ? "warning"
                      : "success"
                }
                sx={{ height: 8, borderRadius: 1, mt: 1.5 }}
              />
              <Stack spacing={0.75} sx={{ mt: 2, maxHeight: 260, overflow: "auto", pr: 0.5 }}>
                {profileCompletion.checks.map((c) => (
                  <CompletionRow
                    key={c.label}
                    label={c.label}
                    done={c.done}
                    important={c.important}
                  />
                ))}
              </Stack>
              {profileCompletion.pct < 100 && (
                <Button
                  component={Link}
                  to="/app/profile"
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ mt: 2 }}
                >
                  Complete profile
                </Button>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Typography variant="h6">Recent activity</Typography>
              </Stack>
              {appsLoading || profileLoading ? (
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              ) : recent.length === 0 ? (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No activity yet. Add your first application.
                  </Typography>
                  <Button
                    component={Link}
                    to="/app/applications"
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1.5 }}
                  >
                    Track an application
                  </Button>
                </Box>
              ) : (
                <Stack divider={<Divider flexItem />}>
                  {recent.map((a) => (
                    <ActivityRow key={a.id} entry={a} />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function StatCard({
  label,
  value,
  icon,
  tint,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tint: string;
}) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(tint, 0.15),
              color: tint,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );
}

function CompletionRow({
  label,
  done,
  important = false,
}: {
  label: string;
  done: boolean;
  important?: boolean;
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      {done ? (
        <VerifiedIcon fontSize="small" color="success" />
      ) : (
        <CancelOutlinedIcon
          fontSize="small"
          sx={{ color: important ? "warning.main" : "text.disabled" }}
        />
      )}
      <Typography
        variant="body2"
        sx={{ color: done ? "text.primary" : "text.secondary" }}
      >
        {label}
        {!done && important ? " *" : ""}
      </Typography>
    </Stack>
  );
}

function ActivityRow({ entry }: { entry: ApplicationEntry }) {
  const meta = STATUS_META[entry.status];
  const date = new Date(entry.appliedAt);
  const dateStr = isNaN(date.getTime())
    ? entry.appliedAt
    : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ alignItems: "center", py: 1.25 }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: "background.default",
          color: "text.primary",
          border: "1px solid",
          borderColor: "divider",
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        {entry.company.slice(0, 1).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {entry.role}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {entry.company} · {dateStr}
        </Typography>
      </Box>
      <Chip label={entry.status} size="small" color={meta.color} variant="outlined" />
    </Stack>
  );
}
